import { SearchOutlined } from '@ant-design/icons'
import { Spin } from 'antd'
import Select from 'antd/lib/select'
import { head, isUndefined } from 'lodash-es'
import isEmpty from 'lodash-es/isEmpty'
import { FunctionComponent, ReactNode, useState } from 'react'
import { useDebounce, useUpdateEffect } from 'react-use'
import styled from 'styled-components'
import { useLazyQuickSearchQuery } from '@/api/workspace-api/search-api'
import KolNameBadge from '@/components/common/kol-name-badge'
import { KolThumbnail } from '@/components/common/kol-thumbnail'
import { NoData } from '@/components/kol/detail/no-data'
import { useIntl } from '@/i18n/hooks/use-intl'
import { QuickSearchKOL } from '@/types/api/search'
import logger from '@/utils/logger'

interface QuickSearchKolInputProps {
  onSelectKol: (kol?: QuickSearchKOL, kolName?: string) => void
  selectedKolUuid?: string
  placeholder?: string
  searchName: string
  onSearchNameChange: (value: string) => void
}

const QuickSearchKolInput: FunctionComponent<QuickSearchKolInputProps> = ({
  onSelectKol,
  selectedKolUuid,
  placeholder,
  searchName,
  onSearchNameChange,
}) => {
  const [kolList, setKolList] = useState<QuickSearchKOL[]>([])
  const { formatMessage, formatNumber } = useIntl()

  const [quickSearch, { isFetching }] = useLazyQuickSearchQuery()

  useDebounce(
    async () => {
      if (isEmpty(searchName)) {
        setKolList([])
        return
      }

      try {
        const response = await quickSearch({
          keyword: searchName,
          limit: 10,
        }).unwrap()

        setKolList(response.data)
      } catch (e) {
        setKolList([])
        logger.error(e)
      }
    },
    500,
    [searchName],
  )

  useUpdateEffect(() => {
    if (isUndefined(selectedKolUuid)) {
      handleOnClear()
    }
  }, [selectedKolUuid])

  const handleOnSearch = (value: string): void => {
    onSearchNameChange(value)
  }

  const handleOnSelect = (uuid?: string): void => {
    const kolName = head(kolList.find((kol) => kol.uuid === uuid)?.name)
    const kol = kolList.find((kol) => kol.uuid === uuid)
    onSelectKol(kol, kolName)

    if (isUndefined(uuid)) {
      handleOnClear()
    }
  }

  const handleOnClear = (): void => {
    onSearchNameChange('')
    setKolList([])
    onSelectKol()
  }

  const renderKOLOption = (kol: QuickSearchKOL): ReactNode => {
    const name = head(kol.name) ?? ''
    const subNames = kol.name
      .slice(1)
      .filter((subName) => subName.includes(searchName))
      .join(' | ')

    return (
      <Select.Option key={kol.uuid} title={name} value={kol.uuid}>
        <KOLWrapper>
          <KolThumbnail align='center' imageSize='small' kolUuid={kol.uuid} />
          <KOLTextWrapper>
            <KolNameBadge
              isRadarStar={kol.isRadarStar}
              isVerified={kol.isVerified}
              name={name}
            />
            <SubInfoWrapper>
              {subNames && <KOLSubNames>{subNames}</KOLSubNames>}
              <Follower>
                {formatNumber(kol.followerCount)}
                {formatMessage({ id: 'kol:platform_data_follower' })}
              </Follower>
            </SubInfoWrapper>
          </KOLTextWrapper>
        </KOLWrapper>
      </Select.Option>
    )
  }

  return (
    <Wrapper>
      <StyledQuickSearchKolSelect
        allowClear
        showSearch
        filterOption={false}
        notFoundContent={<NoData />}
        placeholder={
          placeholder ??
          formatMessage({
            id: 'campaign:field_kol_name_placeholder',
          })
        }
        suffixIcon={null}
        value={selectedKolUuid}
        onClear={handleOnSelect}
        onSearch={handleOnSearch}
        onSelect={handleOnSelect}
      >
        {isFetching ? (
          <Select.Option>
            <Spin size='small' />
          </Select.Option>
        ) : (
          kolList.map(renderKOLOption)
        )}
      </StyledQuickSearchKolSelect>
      <SearchOutlined />
    </Wrapper>
  )
}

const Follower = styled.div`
  font-size: 12px;
  line-height: 17px;
  color: #909399;
`

const SubInfoWrapper = styled.div`
  height: 20px;
  display: flex;
  align-items: center;
`

const KOLSubNames = styled.div`
  font-size: 12px;
  line-height: 17px;
  color: #3a3d4c;
  margin-right: 5px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const KOLTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const KOLWrapper = styled.div`
  display: flex;
  align-items: center;

  ${KolThumbnail} {
    margin-right: 16px;
  }
`

const StyledQuickSearchKolSelect = styled(Select)`
  width: 240px;

  .ant-select-selector {
    ${SubInfoWrapper} {
      display: none;
    }
    ${KolThumbnail} {
      span {
        width: 24px !important;
        height: 24px !important;
      }
    }
    ${KOLWrapper} {
      height: 30px;
    }
  }
`

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  .anticon-search {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 10px;
    color: ${({ theme }): string => theme.colors.brand.primary};
  }
  .ant-select-clear {
    right: 30px;
  }
`

export default QuickSearchKolInput
